import React, { useState } from 'react';

import moment from 'moment';
import Mustache from 'mustache';
import { useSelector } from 'react-redux';

import { useTheme } from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import { announcementDeliverySummaryForIdSelector } from '../../reducers/message-selectors';

import { FormattedMessageBody } from '../legacy-components/formatted-message-body';

import { MessageItemFooter } from './message-item-footer';
import { MessageItemHeader } from './message-item-header';

import { HTMLStripper } from '../../util/html-stripper';
import { LanguageCode } from '../../util/languages';
import { MESSAGE_TYPE, MessageType } from '../../util/message-types';
import { useSentMessageTranslation } from '../utils/use-sent-message-translation';

export const failedAnnouncementErrorThreshold = 5;

export type MessageLanguage = {
  languageCode: LanguageCode;
  messageBody: string;
  subject?: string | null;
};

export type MessageItemInputProps = {
  // Display config settings
  isAnnouncementThread?: boolean;
  hideHeaderActions?: boolean;
  isPreview?: boolean;
  // This should largely be unused, but right now we use to show selected / unselected message templates
  // in the template dialogs
  colorOverrideClass?: string;
  className?: string;

  messageBody: string;
  messageType?: MessageType;
  subject?: string | null;

  messageSid?: string;
  senderName?: string;
  recipientName?: string;
  sendDateTime?: string | Date;
  timeZone?: string;
  imageUrls?: string;
  otherLanguages?: Array<MessageLanguage>;
  requiresResponse?: boolean | null;
  announcement?: Announcement | null;

  disableOtherLanguagesMessage?: boolean;
  isInbound?: boolean;
  isFailed?: boolean;
  includesHTML?: boolean;
  doNotTrim?: boolean;
  allowLinkToAnnouncement?: boolean;
  forceAllowEditing?: boolean;
  urlFileMappings?: Array<UrlFileMapping>;
  sentWithVCard?: boolean;
  orgId?: number;
  urlVisitDetails?: { [pathLowerCased: string]: VisitDetail };
  maxCharacters?: number;
  isScheduledToSend?: boolean;

  onEditMessage?: () => void;
  onCancelMessage?: () => void;
  onDeleteMessage?: () => void;

  contactFilesWithUrls?: { contactFile: ContactFile; presignedUrl: string }[];
  messageIdAndLanguageCode?: MessageIdAndLanguageCodeType;

  dataCy?: string;
};

export const MessageItem = ({
  isAnnouncementThread,
  hideHeaderActions,
  isPreview,
  messageType,
  messageSid,
  isInbound,
  imageUrls,
  senderName,
  sendDateTime,
  timeZone,
  messageBody,
  requiresResponse,
  isFailed,
  otherLanguages,
  onEditMessage,
  onCancelMessage,
  colorOverrideClass,
  className,
  onDeleteMessage,
  includesHTML,
  doNotTrim,
  maxCharacters,
  disableOtherLanguagesMessage,
  announcement,
  forceAllowEditing,
  allowLinkToAnnouncement,
  urlFileMappings,
  sentWithVCard,
  orgId,
  isScheduledToSend,
  urlVisitDetails,
  contactFilesWithUrls,
  dataCy,
  messageIdAndLanguageCode,
  recipientName,
  subject,
}: MessageItemInputProps) => {
  const {
    disableTranslateButton,
    disableTranslateButtonMessage,
    shouldShowTranslation,
    hideTranslation,
    loading: translationLoading,
    translatedMessageBody,
    translatedSubject,
    onTranslateMessageClick,
  } = useSentMessageTranslation({
    messageId: messageIdAndLanguageCode?.messageId,
    messageBody,
  });

  const [showFullMessage, setShowFullMessage] = useState<boolean>(false);
  // only used for outbound bulk messages sent in multiple languages
  const [displayLanguage, setDisplayLanguage] = useState<LanguageCode>('en');

  const announcementDeliverySummary = useSelector((state: any) =>
    announcement
      ? announcementDeliverySummaryForIdSelector(state, announcement.announcementId)
      : null,
  );

  const theme = useTheme();
  const isOnMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const handleShowFullMessageClick = () => setShowFullMessage(!showFullMessage);

  const onOtherLanguageClick = (languageCode: LanguageCode) => () => {
    setDisplayLanguage(languageCode);
  };

  const allLanguages = (otherLanguages || []).concat({
    languageCode: 'en',
    messageBody,
    subject,
  });
  const hiddenLanguages = allLanguages.filter(
    ({ languageCode }) => languageCode !== displayLanguage,
  );

  let messageBodyToUse;
  let subjectToUse;
  // outbound bulk message sent in multiple languages
  if (!isInbound && otherLanguages) {
    ({ messageBody: messageBodyToUse, subject: subjectToUse } = allLanguages.find(
      ({ languageCode }) => languageCode === displayLanguage,
    ) || { messageBody, subject });
  } else {
    messageBodyToUse =
      shouldShowTranslation && translatedMessageBody ? translatedMessageBody : messageBody;
    subjectToUse = shouldShowTranslation && translatedSubject ? translatedSubject : subject;
  }

  // only show previews for the files referred to in the message
  const urlKeysInMessage = Mustache.parse(messageBodyToUse).reduce<Array<string>>(
    (acc, spans) => [...acc, spans[1]],
    [],
  );
  const urlMappingsForLanguage = urlFileMappings?.filter((m) =>
    urlKeysInMessage.includes(m.urlKey),
  );
  // Used in the UiNotification when a message is scheduled. Message is trimmed so the notification
  // toast doesn't take up a lot of screen real estate.
  const showDidTruncateMessage =
    (forceAllowEditing || !sendDateTime || moment(sendDateTime).isAfter(moment())) &&
    (!!onEditMessage || !!onCancelMessage);

  const hasHtml = HTMLStripper.isHTML(messageBody);
  const didTruncateMessage = maxCharacters !== undefined && messageBodyToUse.length > maxCharacters;
  const hasSpanAddedToMessage = didTruncateMessage && !showFullMessage;
  const truncatedMessage = hasSpanAddedToMessage
    ? `${messageBodyToUse.slice(0, maxCharacters)}<span>...</span>`
    : messageBodyToUse;

  const getBackgroundColorClass = () => {
    if (colorOverrideClass) {
        return colorOverrideClass;
    }

    if (isInbound) {
      return 'u-background-white';
    }

    const failedAnnouncementPercentage =
      isAnnouncementThread && announcementDeliverySummary
        ? (announcementDeliverySummary.failedCount / announcementDeliverySummary.recipientCount) *
          100
        : 0;
    if (isFailed || failedAnnouncementPercentage > failedAnnouncementErrorThreshold) {
      return 'u-flexGrow-1 u-background-errorMuted u-border-error';
    }

    if (isScheduledToSend) {
      return 'u-background-neutral3';
    }

    return 'u-background-actionMuted';
  };

  const isEmail = messageType === MESSAGE_TYPE.EMAIL;

  return (
    <div className="u-margin-y-sm" data-cy="message-item">
      <div
        className={`u-borderRadius-15 u-border u-border-neutral6 u-position-relative ${getBackgroundColorClass()} ${className}`}
      >
        <div className="u-margin">
          <MessageItemHeader
            messageSid={messageSid}
            messageType={messageType}
            senderName={senderName}
            recipientName={recipientName}
            sendDateTime={sendDateTime}
            subject={subjectToUse}
            orgId={orgId}
            timeZone={timeZone}
            sentWithVCard={!!sentWithVCard}
            isScheduledToSend={!!isScheduledToSend}
            forceAllowEditing={!!forceAllowEditing}
            announcement={announcement}
            onEditMessage={onEditMessage}
            onCancelMessage={onCancelMessage}
            onDeleteMessage={onDeleteMessage}
            allowLinkToAnnouncement={!!allowLinkToAnnouncement}
            disableOtherLanguagesMessage={!!disableOtherLanguagesMessage}
            otherLanguages={otherLanguages}
            hiddenLanguages={hiddenLanguages}
            onOtherLanguageClick={onOtherLanguageClick}
            hideHeaderActions={!!hideHeaderActions}
            isAnnouncementThread={isAnnouncementThread}
          />
          <div className="u-flexDirection-column">
            <FormattedMessageBody
              className={`${
                isEmail && !isOnMobile ? 'u-maxHeight-300px' : 'u-maxHeight-150px'
              } u-overflowY-auto u-wordBreak-breakWord u-whiteSpace-preWrap`}
              messageBody={truncatedMessage}
              includesHTML={hasSpanAddedToMessage || hasHtml || includesHTML}
              shouldTrim={!isEmail && !doNotTrim}
              urlVisitDetails={urlVisitDetails}
              dataCy={dataCy}
            />
            <MessageItemFooter
              isAnnouncementThread={!!isAnnouncementThread}
              announcement={announcement}
              announcementDeliverySummary={announcementDeliverySummary}
              messageSid={messageSid}
              messageType={messageType}
              isFailed={isFailed}
              messageIdAndLanguageCode={messageIdAndLanguageCode}
              disableTranslateButton={disableTranslateButton}
              disableTranslateButtonMessage={disableTranslateButtonMessage}
              shouldShowTranslation={shouldShowTranslation}
              onTranslateMessageClick={onTranslateMessageClick}
              translationLoading={translationLoading}
              hideTranslation={hideTranslation}
              isScheduledToSend={isScheduledToSend}
              isInbound={isInbound}
              imageUrls={imageUrls}
              contactFilesWithUrls={contactFilesWithUrls}
              requiresResponse={requiresResponse}
              urlMappingsForLanguage={urlMappingsForLanguage}
              showDidTruncateMessage={showDidTruncateMessage}
              didTruncateMessage={didTruncateMessage}
              showFullMessage={showFullMessage}
              handleShowFullMessageClick={handleShowFullMessageClick}
              isPreview={isPreview}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
